import axios from "axios";
import React, { useState, useEffect, useMemo } from "react";
import { MDBInput, MDBTabsPane } from "mdb-react-ui-kit";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import RequestorInfo from "../Info/RequestorInfo/RequestorInfo";
import { usePdfUrls } from "../../../hooks/usePdfUrls";
import { AmendmentsExport } from "./AmendmentsExport";

const AmendmentLink = ({ id, fileName, handleDocumentClick }) => {
    return (
        <a
            href="#"
            style={{ 
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                maxWidth: '100%'
            }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation(); // Prevent event bubbling
                handleDocumentClick(id, e);
            }}
            onTouchStart={(e) => {
                e.stopPropagation(); // Prevent touch event bubbling
            }}
        >
            {fileName}
        </a>
    );
};

const Amendments = ({ active, bill, ordinal, legislatureOrdinal }) => {
    const { handleDocumentClick, fetchPdfUrl } = usePdfUrls();
    const [amendmentsData, setAmendmentsData] = useState([]);
    const [staffMembers, setStaffMembers] = useState([]);
    const [legislators, setLegislators] = useState([]);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        rawAmendment: { value: null, matchMode: FilterMatchMode.CONTAINS },
        rawCondensed: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.CONTAINS },
        rawDrafter: { value: null, matchMode: FilterMatchMode.CONTAINS },
        rawRequestor: { value: null, matchMode: FilterMatchMode.CONTAINS }, 
    });
    const [sortField, setSortField] = useState("rawAmendment");
    const [sortOrder, setSortOrder] = useState(-1);
    const [currentData, setCurrentData] = useState([]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    const params = useMemo(() => {
        const paramsObj = new URLSearchParams();
        paramsObj.append("legislatureOrdinal", `${legislatureOrdinal}`);
        paramsObj.append("sessionOrdinal", `${ordinal}`);
        paramsObj.append("billType", `${bill.billType.code}`);
        paramsObj.append("billNumber", `${bill.billNumber}`);
        return paramsObj;
    }, [ordinal, legislatureOrdinal, bill.billType.code, bill.billNumber]);

    // Regex to detect any pattern like (1), (2), (3), etc.
    const numberInParens = /\(\d+\)/;

    useEffect(() => {
        async function fetchStaffMembers() {
            try {
                const response = await axios.get(`${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/staffMembers`);
                setStaffMembers(response.data);
            } catch (error) {
                console.error("Error fetching staff members: ", error);
            }
        }
        async function fetchLegislators() {
            try {
                const response = await axios.get(`${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators`);
                setLegislators(response.data);
            } catch (error) {
                console.error("Error fetching legislators: ", error);
            }
        }
        if (active) {
            fetchStaffMembers();
            fetchLegislators()
        }
    }, [active]);

    useEffect(() => {
        async function fetchData() {
            try {
                // Fetch amendment data from both endpoints
                const [response, amendmentResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_LAWS_BACKEND_HOST}/docs/v1/documents/getBillAmendments?${params.toString()}`),
                    axios.get(`${process.env.REACT_APP_LAWS_BACKEND_HOST}/bills/v1/amendments/findByBillId?billId=${bill.id}`)
                ]);
                
                const amendmentData = amendmentResponse.data.map(amendment => {
                    return {
                        ...amendment,
                        requestorInfo: amendment.requestorId ? { 
                            requestorId: amendment.requestorId,
                            requestorType: "LEGISLATOR"
                        } : null,
                        drafterInfo: amendment.drafterStaffMemberId ? { drafterId: amendment.drafterStaffMemberId } : null,
                        type: amendment.type || ''
                    };
                });

                const condensedVersions = response.data.filter(item =>
                    item.fileName.includes("condensed") && !numberInParens.test(item.fileName)
                );

                const formattedData = response.data
                    .filter(item => !item.fileName.includes("condensed") && !numberInParens.test(item.fileName))
                    .map(item => {
                        const baseFileName = item.fileName.split("_")[0];
                        const condensed = condensedVersions.find(c => c.fileName.startsWith(baseFileName));

                        // Try to match both formats:
                        // 1. With section: HB0002.003.001.O.002
                        // 2. Without section: HB0208.001.001
                        let match = item.fileName.match(/([A-Z]{2})(\d{3,4})\.(\d{3})\.(\d{3})\.([A-Z])\.(\d{3})/);
                        let billType, billNumber, billVersion, section, amendmentVersion;
                        
                        if (match) {
                            // Format with section
                            billType = match[1];
                            billNumber = parseInt(match[2]);
                            billVersion = parseInt(match[3]);  // This is the actual bill version
                            section = match[5];  // This is the section
                            amendmentVersion = parseInt(match[6]);  // This is the amendment version
                        } else {
                            // Format without section
                            match = item.fileName.match(/([A-Z]{2})(\d{3,4})\.(\d{3})\.(\d{3})/);
                            if (match) {
                                billType = match[1];
                                billNumber = parseInt(match[2]);
                                billVersion = parseInt(match[3]);
                                section = null;
                                amendmentVersion = parseInt(match[4]);
                            }
                        }

                        // First try to find an exact match including section
                        let amendmentInfo = amendmentData.find(amendment => {
                            const currentBillNumber = bill.billNumber;
                            let matches = currentBillNumber === billNumber && 
                                        amendment.billVersion === billVersion &&
                                        amendment.number === amendmentVersion;
                            
                            if (section) {
                                matches = matches && amendment.section?.toUpperCase() === section.toUpperCase();
                            }
                            
                            return matches;
                        });

                        // Use fallback check for committee amendments
                        if (!amendmentInfo) {
                            // First check if there's a committee amendment with matching bill number, version, and amendment number
                            const committeeAmendment = amendmentData.find(amendment => {
                                const currentBillNumber = bill.billNumber;
                                return currentBillNumber === billNumber && 
                                       amendment.billVersion === billVersion &&
                                       amendment.number === amendmentVersion &&
                                       amendment.type === 'COMMITTEE';
                            });

                            if (committeeAmendment) {
                                amendmentInfo = committeeAmendment;
                            }
                        }

                        // If no match found, use default values
                        const defaultAmendmentInfo = amendmentInfo || { 
                            type: '',
                            requestorInfo: null,
                            drafterInfo: null
                        };

                        // Map drafter name
                        const drafter = defaultAmendmentInfo.drafterInfo?.drafterId
                          ? staffMembers.find(member => member.id === defaultAmendmentInfo.drafterInfo.drafterId)
                          : null;
                        const rawDrafter = drafter
                          ? `${drafter.lastName || ""}, ${drafter.firstName || ""}`.trim()
                          : '';

                        // Map requester name
                        const requestor = defaultAmendmentInfo.requestorInfo?.requestorId
                            ? legislators.find(leg => leg.id === defaultAmendmentInfo.requestorInfo.requestorId)
                            : null;
                        const rawRequestor = requestor
                            ? `${requestor.lastName || ""}, ${requestor.firstName || ""}`.trim()
                            : '';

                        const result = {
                            rawAmendment: item.fileName,
                            amendment: (
                                <AmendmentLink
                                    id={item.id}
                                    fileName={item.fileName}
                                    handleDocumentClick={handleDocumentClick}
                                />
                            ),
                            amendmentId: item.id,
                            rawCondensed: condensed ? "Yes" : "No",
                            condensed: condensed ? (
                                <AmendmentLink
                                    id={condensed.id}
                                    fileName="Yes"
                                    handleDocumentClick={handleDocumentClick}
                                />
                            ) : "No",
                            condensedId: condensed?.id,
                            drafterInfo: defaultAmendmentInfo.drafterInfo,
                            rawDrafter,
                            requestorInfo: defaultAmendmentInfo.requestorInfo,
                            rawRequestor,
                            type: defaultAmendmentInfo.type || '',  // Ensure type is always present
                        };

                        return result;
                    });

                setAmendmentsData(formattedData);
                setIsDataLoaded(true);
            } catch (error) {
                console.error("Error fetching data: ", error);
                setIsDataLoaded(true);
            }
        }
        if (active) {
            setIsDataLoaded(false);
            fetchData();
           
        }
    }, [params, active, bill.id, staffMembers, legislators]);

    const onGlobalFilterChange = (e) => {
        setFilters({
            ...filters,
            global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS }
        });
    };

    useEffect(() => {
        // Initialize currentData with amendmentsData
        setCurrentData(amendmentsData);
    }, [amendmentsData]);

    useEffect(() => {
        if (isDataLoaded && active) {
            // Function to update accordion height
            const updateAccordionHeight = () => {
                const accordionItem = document.querySelector(`[data-mdb-target="#collapse4"]`);
                if (accordionItem) {
                    const content = accordionItem.nextElementSibling;
                    if (content) {
                        content.style.height = 'auto';
                        // Force a reflow to ensure height is recalculated
                        content.offsetHeight;
                    }
                }
            };

            // Initial update
            setTimeout(updateAccordionHeight, 0);

            // Add resize listener
            window.addEventListener('resize', updateAccordionHeight);

            // Cleanup
            return () => window.removeEventListener('resize', updateAccordionHeight);
        }
    }, [isDataLoaded, active]);

    // Modify handleDocumentClick to use the fetched URL
    const handleAmendmentClick = async (id, e) => {
        e.preventDefault();
        try {
            const url = await fetchPdfUrl(id);
            if (url) {
                // For iOS, we need to handle the URL opening differently
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
                if (isIOS) {
                    // On iOS, we'll redirect the current window
                    window.location.href = url;
                } else {
                    // On other platforms, open in new window
                    window.open(url, '_blank');
                }
            }
        } catch (error) {
            console.error('Error handling amendment click:', error);
        }
    };

    if (!active) return null;

    return (
        <MDBTabsPane show={active}>
            <div className="row g-1">
                <div className="col-sm"></div>
                <div className="col-sm">
                    <MDBInput
                        className="mb-3"
                        label="Search Amendments..."
                        type="text"
                        value={filters.global.value || ""}
                        onChange={onGlobalFilterChange}
                    />
                </div>
                <div className="col-auto">
                    <AmendmentsExport 
                        amendmentsData={currentData || amendmentsData}
                        bill={bill}
                        handlePdfUrlFetch={fetchPdfUrl}
                    />
                </div>
            </div>
            <div style={{ 
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                backgroundColor: 'white'
            }}>
                <DataTable
                    value={amendmentsData}
                    paginator
                    rows={100}
                    rowsPerPageOptions={[25, 50, 100]}
                    paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                    stripedRows
                    columnResizeMode="expand"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={(e) => {
                        setSortField(e.sortField);
                        setSortOrder(e.sortOrder);
                    }}
                    removableSort
                    filters={filters}
                    onValueChange={(e) => setCurrentData(e.data)}
                    className="amendments-table"
                    style={{ 
                        height: isDataLoaded ? 'auto' : '200px',
                        minHeight: '200px',
                        position: 'relative',
                        zIndex: 1,
                        overflow: 'hidden',
                        WebkitOverflowScrolling: 'touch' // Enable smooth scrolling on iOS
                    }}
                    loading={!isDataLoaded}
                >
                    <Column
                        field="rawAmendment"
                        header="Amendment"
                        sortable
                        filter
                        body={(rowData) => (
                            <div style={{ position: 'relative', zIndex: 9999 }}>
                                <AmendmentLink
                                    id={rowData.amendmentId}
                                    fileName={rowData.rawAmendment}
                                    handleDocumentClick={handleAmendmentClick}
                                />
                            </div>
                        )}
                    />
                    <Column
                        field="rawCondensed"
                        header="Condensed Version"
                        sortable
                        filter
                        body={(rowData) => rowData.condensedId ? (
                            <AmendmentLink
                                id={rowData.condensedId}
                                fileName="Yes"
                                handleDocumentClick={handleAmendmentClick}
                            />
                        ) : "No"}
                    />
                    <Column
                        field="type"
                        header="Type"
                        sortable
                        filter
                        body={(rowData) => {
                            const titleCase = rowData.type
                                .toLowerCase()
                                .replace(/\b\w/g, (char) => char.toUpperCase());
                            return titleCase;
                        }}
                    />
                    <Column
                        field="rawDrafter"
                        header="Amendment Drafter"
                        sortable
                        filter
                        body={(rowData) => {
                            if (!rowData.drafterInfo?.drafterId) return '';
                            const drafter = staffMembers.find(member => member.id === rowData.drafterInfo.drafterId);
                            return drafter ? `${drafter.lastName || ''}, ${drafter.firstName || ''}`.trim() : '';
                        }}
                    />
                    <Column
                        field="rawRequestor"
                        header="Requester"
                        sortable
                        filter
                        body={(rowData) =>
                            rowData.requestorInfo?.requestorId ? (
                              <RequestorInfo
                                requestorId={rowData.requestorInfo.requestorId}
                                requestorType={rowData.requestorInfo.requestorType}
                              />
                            ) : ' '
                          }
                    />
                </DataTable>
            </div>
        </MDBTabsPane>
    );
};

export default Amendments;
